import { IonRouterOutlet } from "@ionic/react";
import { Redirect, Route } from "react-router-dom";
import { useOnboardingEnabled } from "@/hooks/use-organization-config";
import { Routes } from "@/lib/router";
import { SettingsAppearancePage } from "@/routes/settings/appearance";
import { SettingsCalendarPage } from "@/routes/settings/calendar";
import { SettingsLanguagePage } from "@/routes/settings/language";
import { SettingsNotificationsPage } from "@/routes/settings/notifications";
import { NotificationsDevicesPage } from "@/routes/settings/notifications/devices";
import { SettingsPersonalInformationPage } from "@/routes/settings/personal-information";
import { PrivacyPage } from "@/routes/settings/privacy";
import { TermsPage } from "@/routes/settings/terms";
import { AuthRouter } from "./auth.router";
import { RouteGuard } from "./route-guard";
import { TabRouter } from "./tab.router";

interface GlobalRouterProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export function GlobalRouter({ isAuthenticated, isAdmin }: GlobalRouterProps) {
  const { enabled: onboardingEnabled } = useOnboardingEnabled();

  // Where signing in lands you. For an employee that is `/app`, which the tab router resolves to
  // Vandaag or Ontdek — deliberately not resolved here as well. Deze router draait al voordat de
  // vlaggen binnen zijn, dus een keuze op deze plek was er altijd één met "alles uit" en zette je
  // op de oude app terwijl de beta gewoon aanstond. Eén plek die het weet, en die wacht erop.
  const defaultAuthPath = isAdmin ? Routes.Settings() : Routes.App();

  // Every protected route goes through `render` with a module-level `RouteGuard`, never through
  // `component={…}` with a wrapper built here. See `route-guard.tsx`: a component type created
  // during render remounts everything below it on every re-render of this router, which is what
  // used to leave Ionic's page stack blank and frozen.
  return (
    <IonRouterOutlet id="global">
      {/* Redirect old accept-invitation route to new auth path for web compatibility */}
      <Route
        exact
        path="/accept-invitation/:token"
        render={({ match }) => <Redirect to={`/auth/accept-invitation/${match.params.token}`} />}
      />

      {/*
        Twee paden die de api uitdeelt en die hier geen route hadden (#199).

        `shift-transfer/<token>` is de deelbare link die een medewerker krijgt als hij zijn dienst
        wil overdragen; `shift-cancellation.service.ts` bouwt hem in de api als custom link met
        precies dat voorvoegsel. Het scherm dat zo'n token int bestaat hier wel, maar heet
        `/app/transfer/:token`.

        `home/projects/<id>` komt uit de knop van een berichtsjabloon in DYNAMIC_PROJECT-modus
        (`message-template.service.ts`). Dat is gewoon de projectkaart, `/app/projects/:id`.

        Allebei dus een naam die naast de deur valt en geen ontbrekend scherm: een redirect, in het
        idioom van `/accept-invitation/:token` hierboven.

        Waarom hier en niet in `resolveDeeplinkPath`: een pad bereikt de router langs twee wegen.
        Een deeplink loopt door die functie, maar een getikte pushmelding duwt zijn `path`
        rechtstreeks in de history (`notification-listener/push.tsx`). Op deze plek gelden ze
        allebei.

        Alleen het pad gaat mee, net als bij `/accept-invitation/:token`. Geen van deze twee links
        draagt een querystring: de api zet het token in het pad. Een passthrough van `search` stond
        hier eerst wel, maar in de testharnas kwam hij nergens aan -- een redirect binnen deze
        outlet verloor hem, net zoals de `callbackUrl` van `RouteGuard` daar verloren gaat. Niet
        bewezen gedrag hoort niet in een hotfix, dus het staat er niet meer in.

        Zonder dit landde zo'n link op het vangnet onderaan, dat een onbekend pad naar `/` stuurt:
        de gebruiker kwam op Vandaag uit en het token of het project was weg.
      */}
      <Route
        exact
        path="/shift-transfer/:token"
        render={({ match }) => (
          <Redirect to={Routes.TransferClaim({ token: match.params.token })} />
        )}
      />
      <Route
        exact
        path="/home/projects/:id"
        render={({ match }) => <Redirect to={Routes.Project({ id: match.params.id })} />}
      />

      {/* Redirect old onboarding route to new auth path */}
      <Route exact path="/onboarding" render={() => <Redirect to={Routes.Onboarding()} />} />

      {/* Auth routes - outside of app context (includes accept-invitation and onboarding) */}
      <Route
        path={Routes.Auth()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated} required={false} authPath={defaultAuthPath}>
            <AuthRouter />
          </RouteGuard>
        )}
      />

      {/* Settings routes */}
      <Route
        exact
        path={Routes.PersonalInformation()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <SettingsPersonalInformationPage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Notifications()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <SettingsNotificationsPage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Calendar()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <SettingsCalendarPage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Appearance()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <SettingsAppearancePage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Language()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <SettingsLanguagePage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.NotificationsDevices()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <NotificationsDevicesPage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Privacy()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <PrivacyPage />
          </RouteGuard>
        )}
      />
      <Route
        exact
        path={Routes.Terms()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <TermsPage />
          </RouteGuard>
        )}
      />

      {/* Main app with tabs - should match last */}
      <Route
        path={Routes.App()}
        render={() => (
          <RouteGuard isAuthenticated={isAuthenticated}>
            <TabRouter />
          </RouteGuard>
        )}
      />

      <Redirect exact from="/settings" to={Routes.Settings()} />

      <Redirect
        exact
        from="/"
        to={
          isAuthenticated
            ? defaultAuthPath
            : onboardingEnabled
              ? Routes.Onboarding()
              : Routes.AuthSignInPassword()
        }
      />

      {/*
        Het vangnet, en de reden dat het geen `path` heeft.

        `IonRouterOutlet` is geen `Switch`: `matchRoute` loopt alle kinderen langs en houdt de
        laatste match over, dus een `path="*"` zou elke route hierboven overrulen. Een kind zonder
        `path` en zonder `from` behandelt Ionic juist als de niet-gevonden route: dat kind komt
        alleen aan de beurt als niets anders matcht (`matchDefaultRoute` in `@ionic/react-router`).

        Zonder dit rendert een onbekend pad niets. Geen foutmelding en geen knop, alleen een wit
        scherm waar je de app voor moet afsluiten, en een deeplink die net naast een route landt is
        precies zo'n pad. `/` weet zelf al waar iemand hoort uit te komen, ingelogd of niet, dus die
        keuze blijft op één plek staan.
      */}
      <Route render={() => <Redirect to="/" />} />
    </IonRouterOutlet>
  );
}
